import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useRef, useState, useMemo, Fragment } from 'react';
import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import {
  FloatingList,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
} from '@floating-ui/react';

import { useClientRect } from '@/lib/hooks/use-client-rect';
import { Icon } from '@/components/icon';
import { Overlay } from '@/components/overlay';

import { Tooltip } from '@/components/tooltip';

import {
  FormInputSize,
  SelectOptions,
  isOptionGroup,
  SelectOption,
  Option,
  OptionValue,
} from '../types';
import { Input } from '../input';

import styles from './search-input.module.scss';

type Value<T> = T | T[];

interface BaseSearchInputProps<T extends OptionValue> {
  options: SelectOptions<T>;
  autoFocus?: boolean;
  className?: classNames.Argument;
  size?: FormInputSize;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  label?: string;
  showShortcuts?: boolean;
  fullWidth?: boolean;
  allowCustomValue?: boolean;
}

export interface SearchInputProps<OV extends OptionValue, V extends Value<OV>>
  extends BaseSearchInputProps<OV> {
  value?: V;
  onChange: (value: V) => void;
  closeOnSelect?: boolean;
}

const getSelectedOptionLabel = <OV extends OptionValue, V extends Value<OV>>({
  value,
  selectedOptionParent,
  options,
}: {
  value?: V;
  placeholder: string;
  selectedOptionParent?: SelectOption<OV>;
  options: SelectOptions<OV>;
}) => {
  const flatOptions = options.flatMap((option) =>
    isOptionGroup(option) ? option.options : option,
  );
  const selectedOption = Array.isArray(value)
    ? flatOptions.find((option) => value.includes(option.value))
    : flatOptions.find((option) => option.value === value);

  if (Array.isArray(value)) {
    if (value.length > 1) {
      return value.join(', ');
    }
    if (value.length === 1) {
      return selectedOption?.label;
    }
  }

  return selectedOptionParent !== undefined
    ? `${selectedOption?.label} (${selectedOptionParent.label})`
    : selectedOption?.label;
};

function SearchInput<OV extends OptionValue, V extends Value<OV> = OV>(
  props: SearchInputProps<OV, V>,
) {
  const {
    options,
    value,
    autoFocus,
    placeholder = 'Search...',
    label,
    onChange,
    size = 'small',
    disabled = false,
    loading = false,
    closeOnSelect = true,
    showShortcuts = false,
    fullWidth = true,
    allowCustomValue = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const triggerRect = useClientRect(triggerRef, [isOpen]);

  const position = {
    top: (triggerRect?.top ?? 0) + (visualViewport?.offsetTop ?? 0), // Adjust for mobile browsers
    left: triggerRect?.left ?? 0,
  };

  const selectedOptionParent = options.find(
    (option) => isOptionGroup(option) && option.options.some((o) => o.value === value),
  );
  const selectedOptionLabel = useMemo(
    () => getSelectedOptionLabel({ value, options, selectedOptionParent, placeholder }),
    [value, options, selectedOptionParent, placeholder],
  );

  const stringValue =
    value === undefined || value === ''
      ? undefined
      : Array.isArray(value)
        ? value.length > 0
          ? value.join(', ')
          : undefined
        : String(value);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, [triggerRef]);

  useEffect(() => {
    if (autoFocus === true) {
      triggerRef.current?.focus();
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={classNames(styles.searchInput, props.className, {
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'regular',
        [styles.sizeLarge]: size === 'large',
        [styles.fullWidth]: fullWidth,
      })}>
      <SearchInputTrigger
        label={
          loading
            ? 'Loading...'
            : (label ?? selectedOptionLabel ?? stringValue ?? placeholder ?? '')
        }
        onOpenPanel={(searchTerm) => {
          setSearchTerm(searchTerm ?? '');
          setIsOpen(!isOpen);
        }}
        ref={triggerRef}
        disabled={disabled || loading}
      />
      {isOpen && (
        <Overlay>
          <div className={styles.backdrop} onClick={() => setIsOpen(false)} />
          <SearchInputPanel<OV, V>
            searchTerm={searchTerm}
            options={options}
            value={value}
            position={position}
            placeholder={placeholder}
            width={triggerRect?.width ?? 0}
            showShortcuts={showShortcuts && Array.isArray(value)}
            onChangeSearchTerm={setSearchTerm}
            allowCustomValue={allowCustomValue}
            onChange={(value) => {
              onChange(value);
              closeOnSelect && closePanel();
            }}
            onClose={closePanel}
            size={size}
          />
        </Overlay>
      )}
    </div>
  );
}

type SearchInputTriggerProps = {
  label: string;
  disabled?: boolean;
  onOpenPanel(searchTerm?: string): void;
};

const SearchInputTrigger = forwardRef(function SearchInputTrigger(
  props: SearchInputTriggerProps,
  ref?: React.ForwardedRef<HTMLButtonElement>,
) {
  const { label, disabled = false, onOpenPanel } = props;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        onOpenPanel();
      }
      if (e.key.length === 1) {
        e.preventDefault();
        onOpenPanel(e.key);
      }
    },
    [onOpenPanel],
  );

  return (
    <button
      className={styles.trigger}
      type="button"
      onClick={() => onOpenPanel()}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      ref={ref}
      title={label}>
      <span className={styles.label}>{label}</span>
      <div className={styles.chevron}>
        <svg
          focusable="false"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true">
          <path d="M8 11L3 6 3.7 5.3 8 9.6 12.3 5.3 13 6z" />
        </svg>
      </div>
    </button>
  );
});

interface SearchInputPanelProps<OV extends OptionValue, V extends Value<OV> = OV> {
  options: SelectOptions<OV> | Readonly<SelectOptions<OV>>;
  searchTerm: string;
  value?: V;
  position: { top: number; left: number };
  width: number;
  placeholder?: string;
  onChangeSearchTerm: (value: string) => void;
  onChange: (value: V) => void;
  onClose?: () => void;
  size?: FormInputSize;
  showShortcuts: boolean;
  allowCustomValue: boolean;
}

const flattenOptions = <OV extends OptionValue>(
  options: SelectOptions<OV> | Readonly<SelectOptions<OV>>,
) => options.flatMap((option) => (isOptionGroup(option) ? option.options : option));

const SearchInputPanel = <OV extends OptionValue, V extends Value<OV> = OV>(
  props: SearchInputPanelProps<OV, V>,
) => {
  const {
    options,
    value,
    position,
    width,
    placeholder,
    searchTerm,
    onChangeSearchTerm,
    onChange,
    onClose,
    size,
    showShortcuts,
    allowCustomValue,
  } = props;

  const [paddedOptions, setPaddedOptions] = useState(() => {
    const flatOptions = flattenOptions(options);
    const existingValues = new Set(flatOptions.map((o) => o.value));
    const valuesToPad: Option<OV>[] = [];

    const addValueToPad = (v: OV) => {
      if (!existingValues.has(v)) {
        valuesToPad.push({ label: String(v), value: v });
        existingValues.add(v);
      }
    };

    if (Array.isArray(value)) {
      value.forEach(addValueToPad);
    } else if (value !== undefined && value !== '') {
      addValueToPad(value as OV);
    }

    if (valuesToPad.length === 0) {
      return options;
    }

    return [...options, ...valuesToPad];
  });

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const filteredValues = useFuzzySearchList<Option<OV>, OV>({
    strategy: 'smart',
    list: flattenOptions<OV>(paddedOptions),
    queryText: searchTerm.trim(),
    getText: (option) => [option.label, option.value.toString()],
    mapResultItem: ({ item }) => item.value,
  });

  const matchCount = filteredValues.length;
  const hasExactMatch = useMemo(
    () =>
      flattenOptions<OV>(paddedOptions).some(
        (option) => option.label.toLowerCase() === searchTerm.trim().toLowerCase(),
      ),
    [paddedOptions, searchTerm],
  );

  const isMatch = (option: SelectOption<OV>) =>
    isOptionGroup(option) || filteredValues.includes(option.value);

  const firstSelectedValueIndex = flattenOptions(paddedOptions)
    .filter(isMatch)
    .findIndex((option) => option.value === value);

  const { refs, context } = useFloating({
    open: true,
  });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    virtual: true,
    loop: true,
    selectedIndex: searchTerm ? activeIndex : firstSelectedValueIndex,
    onNavigate: (index) => setActiveIndex(index),
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNavigation]);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchTerm]);

  const listRect = useClientRect(refs.floating, [searchTerm]);

  const filteredOptions = paddedOptions.filter(isMatch).map((option) => {
    if (isOptionGroup(option)) {
      return {
        ...option,
        options: option.options.filter(isMatch),
      };
    }
    return option;
  });

  const [direction, setDirection] = useState<'up' | 'down'>('down');
  useEffect(() => {
    direction === 'down' &&
      listRect !== null &&
      listRect.top + listRect.height > window.innerHeight &&
      setDirection('up');
  }, [direction, listRect]);

  const handleSelectOption = useCallback(
    (option: Option<OV>) => {
      if (!Array.isArray(value)) {
        return onChange(option.value as V);
      }
      const newValue = value.includes(option.value)
        ? value.filter((v) => v !== option.value)
        : value.concat(option.value);
      onChange(newValue as V);
    },
    [value, onChange],
  );

  const handleSelectAll = useCallback(() => {
    onChange(flattenOptions(paddedOptions).map((option) => option.value) as V);
  }, [onChange, paddedOptions]);

  const handleSelectNone = useCallback(() => {
    onChange([] as unknown as V);
  }, [onChange]);

  const handleCustomOption = useCallback(() => {
    const option = { label: searchTerm, value: searchTerm as OV };
    setPaddedOptions((prevOptions) => [...prevOptions, option]);
    handleSelectOption(option);
  }, [handleSelectOption, searchTerm]);

  const showCustomValueOption = allowCustomValue && searchTerm.trim().length > 0 && !hasExactMatch;

  return (
    <FloatingList elementsRef={listRef}>
      <div
        className={classNames(styles.panel, {
          [styles.sizeSmall]: size === 'small',
          [styles.sizeMedium]: size === 'regular',
          [styles.sizeLarge]: size === 'large',
          [styles.openAbove]: direction === 'up',
        })}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${width}px`,
        }}>
        <Input
          size={size}
          value={searchTerm}
          placeholder={placeholder}
          onChange={(e) => onChangeSearchTerm(e.target.value)}
          ref={refs.setReference}
          autoFocus
          {...getReferenceProps({
            onKeyDown: (e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose?.();
                return;
              }
              const flatOptions = flattenOptions(filteredOptions);
              const activeOption = flatOptions.at(
                activeIndex !== null && activeIndex !== -1 ? activeIndex : 0,
              );

              if (e.key === 'Enter' || e.key === 'Tab') {
                if (activeOption !== undefined) {
                  e.preventDefault();
                  handleSelectOption(activeOption);
                } else if (showCustomValueOption && activeIndex === flatOptions.length) {
                  e.preventDefault();
                  handleSelectOption({ label: searchTerm, value: searchTerm as OV });
                }
              }

              if (
                allowCustomValue &&
                e.key === 'Enter' &&
                activeOption === undefined &&
                searchTerm.trim().length > 0 &&
                !hasExactMatch
              ) {
                e.preventDefault();
                handleCustomOption();
              }
            },
          })}
        />
        <div ref={refs.setFloating} className={styles.items} role="listbox" {...getFloatingProps()}>
          {showShortcuts && (
            <div className={styles.shortcuts}>
              Select:
              <button className={styles.shortcut} onClick={handleSelectAll}>
                All
              </button>{' '}
              /
              <button className={styles.shortcut} onClick={handleSelectNone}>
                None
              </button>
            </div>
          )}

          {matchCount === 0 && !allowCustomValue ? (
            <div className={styles.empty} role="listbox">
              No matches found
            </div>
          ) : (
            <ItemList
              options={filteredOptions}
              onClick={(option) => handleSelectOption(option)}
              getItemProps={getItemProps}
              activeIndex={activeIndex}
              value={value}
            />
          )}
          {showCustomValueOption && (
            <CustomValueItem
              searchTerm={searchTerm}
              activeIndex={activeIndex}
              getItemProps={getItemProps}
              selected={
                Array.isArray(value)
                  ? value.includes(searchTerm as OV)
                  : value === (searchTerm as OV)
              }
              onClick={handleCustomOption}
            />
          )}
        </div>
      </div>
    </FloatingList>
  );
};

type ItemListProps<OV extends OptionValue, V extends Value<OV>> = {
  options: SelectOption<OV>[];
  value?: V;
  activeIndex: number | null;
  onClick: (item: Option<OV>) => void;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

const ItemList = <OV extends OptionValue, V extends Value<OV> = OV>(
  props: ItemListProps<OV, V>,
) => {
  const { options, value } = props;

  return (
    <>
      {options.map((option, i) => {
        if ('options' in option) {
          if (option.options.length === 0) {
            return null;
          }
          return (
            <Fragment key={i}>
              <div className={styles.title}>{option.label}</div>
              <ItemList
                options={option.options}
                onClick={props.onClick}
                activeIndex={props.activeIndex}
                getItemProps={props.getItemProps}
                value={props.value}
              />
            </Fragment>
          );
        }
        const isSelected = Array.isArray(value)
          ? value.includes(option.value)
          : option.value === value;
        return (
          <Item
            key={i}
            option={option}
            onClick={props.onClick}
            selected={isSelected}
            activeIndex={props.activeIndex}
            getItemProps={props.getItemProps}
          />
        );
      })}
    </>
  );
};

type ItemProps<OV extends OptionValue> = {
  option: Option<OV>;
  selected?: boolean;
  activeIndex: number | null;
  onClick: (option: Option<OV>) => void;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

const Item = <OV extends OptionValue>(props: ItemProps<OV>) => {
  const { option, activeIndex, selected = false } = props;
  const { ref, index } = useListItem();

  const buttonElem = (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      title={'title' in option ? option.title : option.label}
      className={classNames(styles.item, {
        [styles.selected]: selected,
        [styles.active]: index === activeIndex && activeIndex !== -1,
      })}
      disabled={option.disabled}
      {...props.getItemProps({
        onClick: () => props.onClick(option),
      })}>
      {option.icon !== undefined && <Icon size={16} name={option.icon} />}
      <span className={styles.label}>{option.label}</span>
      {selected && <Icon size={16} name="Check" className={styles.check} />}
    </button>
  );

  return (
    <div>
      {option.tooltip === undefined ? (
        <div ref={ref}>{buttonElem}</div>
      ) : (
        <Tooltip
          ref={ref}
          placement="left"
          color="light"
          hasArrow={false}
          delay={0}
          content={option.tooltip}
          open={activeIndex === index}
          trigger={null}
          hideIfReferenceHidden>
          {buttonElem}
        </Tooltip>
      )}
    </div>
  );
};

interface CustomValueItemProps {
  searchTerm: string;
  activeIndex: number | null;
  selected: boolean;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  onClick: () => void;
}

const CustomValueItem = (props: CustomValueItemProps) => {
  const { searchTerm, activeIndex, getItemProps, onClick, selected } = props;
  const { ref, index } = useListItem();

  return (
    <div ref={ref}>
      <button
        type="button"
        role="option"
        aria-selected={selected}
        className={classNames(styles.item, {
          [styles.selected]: selected,
          [styles.active]: index === activeIndex && activeIndex !== -1,
        })}
        {...getItemProps({ onClick })}>
        <span className={styles.label}>Use custom value: &quot;{searchTerm}&quot;</span>
        {selected && <Icon size={16} name="Check" className={styles.check} />}
      </button>
    </div>
  );
};

export { SearchInput };
